import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { CloseIconButton2, Modal, PageLoading } from "@tripian/react";
import { providers } from "@tripian/core";
import Model, { Providers } from "@tripian/model";
import ICombinedState from "../../redux/model/ICombinedState";
import { changeTripListVisible } from "../../redux/action/layout";
import { changePlans } from "../../redux/action/trip";
import {
  TRIP_PLAN,
  UPDATE_TRIP,
  LOGIN /*, TOUR_INFO , MY_BOOKINGS_PAGE , LOCAL_EXPERIENCES */,
  USER_PROFILE,
  TRIPS,
} from "../../constants/ROUTER_PATH_TITLE";
import MapLayout from "./layouts/MapLayout/MapLayout";
import Menu from "./layouts/Menu/Menu";
import PlanContainer from "./layouts/TripLayout/Container/PlanContiner/PlanContainer";
import FocusContainer from "./layouts/TripLayout/Container/FocusContainer/FocusContainer";
import SearchContainer from "./layouts/TripLayout/Container/SearchContainer/SearchContainer";
import FavoritesContainer from "./layouts/TripLayout/Container/FavoritesContainer/FavoritesContainer";
import KiwiFavoritesContainer from "./layouts/TripLayout/Container/KiwiFavoritesContainer/KiwiFavoritesContainer";
import MyBookingsContainer from "./layouts/TripLayout/Container/MyBookingsContainer/MyBookingsContainer";
import * as gtag from "../../gtag";

import useAuth from "../../hooks/useAuth";
import useTrip from "../../hooks/useTrip";
import usePlan from "../../hooks/usePlan";
import useAlternative from "../../hooks/useAlternative";
import usePoiCategory from "../../hooks/usePoiCategory";
import useFocus from "../../hooks/useFocus";
import useLayoutPlan from "./hooks/useLayoutPlan";
import AppNav from "../../App/AppNav/AppNav";
import GuestShareHeader from "../../components/ShareTripModal/GuestShareHeader";
import TripAppMenu from "../../App/AppNav/TripMenu/TripAppMenu";
import { changeReference } from "../../redux/action/trip";

import useUser from "../../hooks/useUser";
import useTranslate from "../../hooks/useTranslate";
import MyOffersContainer from "./layouts/TripLayout/Container/MyOffersContainer/MyOffersContainer";
import SearchOffersContainer from "./layouts/TripLayout/Container/SearchOffersContainer/SearchOffersContainer";
import useSearchOffer from "../../hooks/useSearchOffer";
import useTripHashParams from "../../hooks/useTripHashParams";
import useMyOffers from "../../hooks/useMyOffers";
import useTravelify from "../../hooks/useTravelify";
import { LoginModal } from "../../components/LoginModal/LoginModal";
import TripLayout from "./layouts/TripLayout/TripLayout";
import { CloneModal } from "../../components/CloneModal/CloneModal";
import ShareTripModal from "../../components/ShareTripModal/ShareTripModal";
import StoragePoiModal from "../../components/CustomPoiModal/StoragePoiModal";
import BookableExperiencesContainer from "./layouts/TripLayout/Container/BookableExperiencesContainer/BookableExperiencesContainer";
import BookableExperiencesContainerLegacy from "./layouts/TripLayout/Container/BookableExperiencesContainer/BookableExperiencesContainerLegacy";
import isTourApiEnabled from "../../utils/isTourApiEnabled";
import TravelifyContainer from "../../components/TravelifyContainer/TravelifyContainer";
import VictoryProductInfo from "../TourInfo/VictoryProductInfo/VictoryProductInfo";
import VistorTourInfo from "../TourInfo/VistorTourInfo/VistorTourInfo";
import GygTourInfo from "../TourInfo/GygTourInfo/GygTourInfo";
import ToristyTourInfo from "../TourInfo/ToristyTourInfo/ToristyTourInfo";
import LivnTourInfo from "../TourInfo/LivnTourInfo/LivnTourInfo";
import { useGuestUser } from "../../hooks/useGuestUser";
import useKiwiSearch from "../../hooks/useKiwiSearch";
import useCities from "../../hooks/useCities";
import KiwiHotelContainer from "./layouts/TripLayout/Container/KiwiHotelContainer/KiwiHotelContainer";

import classes from "./TripPlan.module.scss";

const mapClasses = ["map-container"];

/**
 * Detayı uygulama içinde modal olarak açılan provider'lar. Aşağıda bu provider'lar
 * için bir TourInfo bileşeni render ediliyor; listeye provider eklerken modalını da
 * eklemek gerekiyor. ToursandTickets sayfasındaki OVERLAY_PROVIDER_IDS ile aynı liste.
 */
const MODAL_PROVIDER_IDS: Model.PROVIDER_ID[] = [
  Model.PROVIDER_ID.GYG,
  Model.PROVIDER_ID.VIATOR,
  Model.PROVIDER_ID.TORISTY,
  Model.PROVIDER_ID.VICTORY,
  Model.PROVIDER_ID.LIVN,
];

// Referansı stabil boş listeler: her render'da yeni array üretip memo'lu kartları boşuna yenilemesin
const EMPTY_TOUR_IDS = {
  gyg: [] as number[],
  viator: [] as string[],
  toristy: [] as string[],
  bb: [] as number[],
};

const TripPlanPage = () => {
  const { tripListVisible, tripHash, shared } = useSelector(
    (state: ICombinedState) => ({
      tripListVisible: state.layout.tripListVisible,
      tripHash: state.trip?.reference?.tripHash,
      shared: state.trip.reference?.shared,
    })
  );
  const { isLoggedIn, loadingIsLoggedIn } = useAuth();
  const { loadingTripReferences } = useUser();
  const { loadingTripReference, tripShare, tripReadOnly, tripFetch } = useTrip();

  const { plans, planFetch, loadingPlan, loadingPlans } = usePlan();
  const { alternatives, alternativesFetch } = useAlternative();
  const { poiCategoryGroups, loadingPoiCategories } = usePoiCategory();
  const [selectedPoiCategoryIds, setSelectedPoiCategoryIds] = useState<
    number[]
  >([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [kiwiFavoritesTab, setKiwiFavoritesTab] = useState<"favorites" | "bookings" | "offers">("favorites");

  const { focusLost, focus } = useFocus();
  const { loadingSearchOffers, offersResult, searchOffer } = useSearchOffer();
  const {
    isLoadingOffer,
    myAllOffers,
    offerOptIn,
    offerOptOut,
    loadingMyAllOffers,
  } = useMyOffers();
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
  const [showShareTripModal, setShowShareTripModal] = useState(false);
  const [shareTripModalLoading, setShareTripModalLoading] =
    useState<boolean>(false);
  const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
  const [showStoragePoiModal, setShowStoragePoiModal] =
    useState<boolean>(false);
  const [storageModalData, setStorageModalData] = useState<{
    storageName: string;
    storageCoordinate: Model.Coordinate;
    storageAddress: string;
  } | null>(null);
  const {
    favoritesVisible,
    setFavoritesVisible,
    yourBookingsVisible,
    setYourBookingsVisible,
    offersMyVisible,
    setOffersMyVisible,
    offersSearchVisible,
    setOffersSearchVisible,
  } = useLayoutPlan();

  const [isBookingClicked, setIsBookingClicked] = useState<boolean>(false);

  const { t } = useTranslate();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { hashParam, dayIndex } = useParams<{
    hashParam?: string;
    dayIndex?: string;
  }>();

  const dayIndexNumber = useMemo(() => {
    const index = Number(dayIndex);
    return isNaN(index) ? 0 : index;
  }, [dayIndex]);

  const { hash, tripReference } = useTripHashParams(hashParam);

  const isGuest = useGuestUser();

  const isFocusOpen = !!focus?.step || !!focus?.poi || !!focus.providerTour;

  const tripUrl = useMemo(() => {
    if (!tripReference) return "";
    return `${window.location.origin}/trip/${tripReference.tripHash}!s/${dayIndexNumber}`;
  }, [tripReference, dayIndexNumber]);

  useEffect(() => {
    if (!isFocusOpen || imageModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        focusLost();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFocusOpen, imageModalOpen, focusLost]);

  // Dil değişikliğini dinle
  useEffect(() => {
    const handleLanguageChange = async () => {
      if (!tripReference?.tripHash) return;

      focusLost();

      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          await tripFetch(tripReference.tripHash, dayIndexNumber, false, attempt < maxRetries);
          break;
        } catch {
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, [tripReference, dayIndexNumber, tripFetch, focusLost]);

  // useAllTourIds şehir katalogunun tamamını sayfa sayfa indiriyordu (arkaplanda
  // yüzlerce tour search isteği); Book rozeti artık backend'in poi.hasBookings
  // flag'inden geldiği ve bu listeler hiçbir tüketicide okunmadığı için boş sabit geçiliyor.
  const gygTourIds = EMPTY_TOUR_IDS.gyg;
  const viatorTourIds = EMPTY_TOUR_IDS.viator;
  const toristyTourIds = EMPTY_TOUR_IDS.toristy;
  const bbTourIds = EMPTY_TOUR_IDS.bb;
  const toursLoading = false;

  const { travelifyUrl, loadingTravelify } = useTravelify(
    tripReference?.city.coordinate.lat,
    tripReference?.city.coordinate.lng,
    tripReference?.tripProfile.arrivalDatetime,
    tripReference?.tripProfile.departureDatetime,
    tripReference?.tripProfile.numberOfAdults,
    Number(tripReference?.tripProfile.numberOfChildren)
  );

  const { cities } = useCities();

  const {
    kiwiHotels,
    loadingKiwiSearch,
    loadingMore: kiwiLoadingMore,
    hasNextPage: kiwiHasNextPage,
    totalItems: kiwiTotalItems,
    destination: kiwiDestination,
    setDestination: setKiwiDestination,
    checkInDate: kiwiCheckIn,
    setCheckInDate: setKiwiCheckIn,
    checkOutDate: kiwiCheckOut,
    setCheckOutDate: setKiwiCheckOut,
    adults: kiwiAdults,
    setAdults: setKiwiAdults,
    childrenCount: kiwiChildren,
    setChildrenCount: setKiwiChildren,
    sortBy: kiwiSortBy,
    setSortBy: setKiwiSortBy,
    filters: kiwiFilters,
    setFilters: setKiwiFilters,
    searchKiwiHotels,
    loadMore: kiwiLoadMore,
  } = useKiwiSearch(
    tripReference?.city.id,
    tripReference?.city.name,
    tripReference?.tripProfile.arrivalDatetime,
    tripReference?.tripProfile.departureDatetime,
    tripReference?.tripProfile.numberOfAdults,
    Number(tripReference?.tripProfile.numberOfChildren)
  );


  document.title = `${TRIP_PLAN.TITLE(t("tripPlan.header"))} - ${tripReference?.city.name || ""} - ${hash}`;

  const sharedTrip = useMemo(() => {
    if (!hashParam) return false;
    const params = hashParam.split("!");
    return params.length > 1 && params[1] === "s";
  }, [hashParam]);

  const hashLogin = useMemo(() => {
    if (!hashParam) return false;
    const params = hashParam.split("!");
    return params.length > 1 && params[1] === "l";
  }, [hashParam]);

  /* */
  /* */
  /* ******* USE STATES ******* */
  /* */

  // Alternative step Id
  const [alternativesStepId, setAlternativesStepId] = useState<
    number | undefined
  >(undefined);

  //The index props for selecting the clicked explore more category
  // const [selectedPoiCategoryGroups, setSelectedPoiCategoryGroups] = useState<Model.PoiCategoryGroup[]>([]);

  /* */
  /* */
  /* ******* USE EFFECTS ******* */
  /* */

  useEffect(() => {
    // Auth yüklenene kadar bekle, yoksa hemen login'e yönlendirir
    if (loadingIsLoggedIn) return;

    if (!isLoggedIn && !sharedTrip && !hashLogin && !isGuest) {
      navigate(LOGIN.PATH, {
        replace: true,
        state: { fromTripPlan: true, tripHash: hashParam },
      });
      return;
    }
  }, [isLoggedIn, loadingIsLoggedIn, sharedTrip, hashLogin, navigate, isGuest, hashParam]);

  useEffect(() => {
    if (tripReference?.city.name) {
      gtag.googleAnalyticsDimension(
        "displayedTripCityName",
        tripReference?.city.name
      );
    }
  }, [tripReference?.city.name]);

  // Clear
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      focusLost();
      // dispatch(changeFavoritesVisible(false));
      // dispatch(changeSearchVisible(false));
    };
  }, [dispatch, focusLost]);

  useEffect(() => {
    if (tripReference?.tripProfile.doNotGenerate === 1) {
      navigate(`${UPDATE_TRIP.PATH}/${hashParam}`, { replace: true });
    }
  }, [
    hashParam,
    navigate,
    tripReference?.tripProfile.doNotGenerate,
    loadingIsLoggedIn,
  ]);

  // Trip Plans
  useEffect(() => {
    if (
      plans?.[dayIndexNumber]?.id &&
      plans[dayIndexNumber]?.generatedStatus === 0 &&
      tripReadOnly === false
    ) {
      planFetch(plans[dayIndexNumber].id);
    }
    setAlternativesStepId(undefined);
  }, [tripReadOnly, dayIndexNumber, planFetch, plans]);

  // Alternatives
  useEffect(() => {
    if (plans) {
      alternativesFetch(dayIndexNumber);
    }
  }, [alternativesFetch, dayIndexNumber, plans]);

  // Alternative Step Id
  useEffect(() => {
    setAlternativesStepId(undefined);
  }, [dayIndexNumber]);

  // GoogleMaps clear last route rendered bounds
  useEffect(() => {
    window.twindow.cityBounds = undefined;
    window.twindow.planBounds = undefined;
  }, []);

  useEffect(() => {
    if (isFocusOpen) {
      setIsBookingClicked(false);
    }
  }, [isFocusOpen]);

  /* */
  /* ******* USE EFFECTS ******* */
  /* */
  /* */

  /* */
  /* */
  /* ******* RENDER ******* */
  /* */
  // class name
  if (!tripListVisible) mapClasses.push("w-m-100");


  // const showLocalExperiences = () => {
  //   if (window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.TRAVELIFY) && providers.travelify && tripReference) {
  //     providers.travelify
  //       .getTourTicketUrl(
  //         tripReference.tripProfile.arrivalDatetime,
  //         tripReference.tripProfile.departureDatetime,
  //         tripReference.city.coordinate.lat,
  //         tripReference.city.coordinate.lng,
  //         10
  //       )
  //       .then((url) => {
  //         window.open(url);
  //       });
  //   } else {
  //     const startDate = moment(tripReference?.tripProfile.arrivalDatetime).format("YYYY-MM-DD");
  //     const endDate = moment(tripReference?.tripProfile.departureDatetime).format("YYYY-MM-DD");

  //     navigate(
  //       `${LOCAL_EXPERIENCES.PATH}?city_id=${tripReference?.city.id}&city_name=${tripReference?.city.name}&lat=${tripReference?.city.coordinate.lat}&lng=${tripReference?.city.coordinate.lng}&start_date=${startDate}&end_date=${endDate}&adult=${tripReference?.tripProfile.numberOfAdults}&children=${tripReference?.tripProfile.numberOfChildren}`,
  //       {
  //         customState: `${TRIP_PLAN.PATH}/${hashParam}`,
  //       }
  //     );
  //   }
  // };

  const switchCheckedOnchange = (checked: boolean) => {
    if (tripReference) {
      if (tripHash && shared !== undefined) {
        setShareTripModalLoading(true);
        tripShare(tripHash, checked).finally(() => {
          setShareTripModalLoading(false);
          dispatch(changeReference({ ...tripReference, shared: checked }));
        });
      }
    }
  };

  const videreoModal = useMemo(() => {
    if (focus.providerVideo) {
      return (
        <Modal
          className="!w-[90%] !max-w-[100vw] bg-background-color md:!w-[70%] md:!max-w-[70vw] max-h-[90vh] overflow-auto"
          show={!!focus.providerVideo}
          backdropClick={() => {
            focusLost();
          }}
          zIndex={499}
        >
          <div className="p-4">
            <div>
              <CloseIconButton2
                fill="#2B2B33"
                clicked={() => {
                  focusLost();
                }}
                rounded
              />
            </div>

            <div>
              <div className="flex items-center justify-center font-bold text-xl me-1">
                {focus.providerVideo?.name}
              </div>

              <div className="mt-4">
                <video
                  controls
                  autoPlay
                  muted
                  className="w-full h-auto max-h-[70vh] object-contain"
                  src={`https://stream.mux.com/${focus.providerVideo.mux_playbackid}/capped-1080p.mp4`}
                />
              </div>
            </div>
          </div>
        </Modal>
      );
    }
  }, [focus.providerVideo, focusLost]);

  const closeAllContainers = () => {
    setFavoritesVisible(false);
    setYourBookingsVisible(false);
    setOffersMyVisible(false);
    setOffersSearchVisible(false);
  };

  const openContainer = (containerSetter: (value: boolean) => void) => {
    closeAllContainers();
    containerSetter(true);
  };

  const [showTourModal, setShowTourModal] = useState(false);
  const [selectedTour, setSelectedTour] = useState<{
    provider: Model.PROVIDER_ID;
    tourId: string;
    cityId?: number;
    cityName?: string;
    startDate?: string;
    endDate?: string;
    adults?: number;
    children?: number;
  } | null>(null);

  useEffect(() => {
    if (sharedTrip || isGuest) return;

    const handlePopState = () => {
      if (showTourModal) {
        setShowTourModal(false);
      } else {
        navigate(TRIPS.PATH, { replace: true });
      }
    };

    const currentState = window.history.state;
    const newState = { ...currentState, fromCustomPage: true };
    window.history.pushState(newState, "", window.location.href);

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [showTourModal, sharedTrip, isGuest, navigate]);

  const handleTourClick = (
    provider: Model.PROVIDER_ID,
    tourId: string,
    additionalData?: any
  ) => {
    if (!window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(provider)) return;

    gtag.googleAnalyticsEvent("tour_modal_open", { provider, tour_id: tourId });

    // Modalı olmayan her provider ürünün kendi sayfasına gider. Bu ayrım allowlist
    // olmak zorunda: denylist tutulduğunda listeye eklenmeyen provider modal state'ine
    // düşüyor, aşağıda o provider için render edilen bir modal olmadığı için de
    // tıklama sessizce kayboluyordu (OccasionGenius'ta yaşanan buydu).
    // Victory Live tourApi'den kendi url'iyle gelir; modalı ise doğrudan Victory API'sini
    // (providers.victory) ister ve o çoğu sitede başlatılmıyor. Url varsa ürün sayfasını aç.
    const victoryWithUrl =
      provider === Model.PROVIDER_ID.VICTORY && !!additionalData?.url;
    if (!MODAL_PROVIDER_IDS.includes(provider) || victoryWithUrl) {
      // Rezdy'de ürün url'i yoksa booking widget'ı productCode'dan kurulabiliyor
      // (FocusContainer ve RezdyTourInfo da aynı url'i üretiyor).
      const externalUrl =
        additionalData?.url ||
        (provider === Model.PROVIDER_ID.REZDY
          ? `https://365adventures.rezdy.com/calendarWidget/${tourId}`
          : undefined);
      if (externalUrl) window.open(externalUrl, "_blank");
      return;
    }

    // Trip bağlamı (şehir, tarihler, yolcu sayısı) her modal provider'a gitmeli:
    // Viator traveler sayılarını, Livn tarihleri bundan dolduruyor. Çağıran taraf
    // kendi değerlerini gönderirse (FocusContainer) onlar öncelikli.
    const selected = {
      provider,
      tourId,
      cityId: tripReference?.city.id,
      cityName: tripReference?.city.name,
      startDate: tripReference?.tripProfile.arrivalDatetime,
      endDate: tripReference?.tripProfile.departureDatetime,
      adults: tripReference?.tripProfile.numberOfAdults,
      children: tripReference?.tripProfile.numberOfChildren || undefined,
      ...additionalData,
    };
    setSelectedTour({ ...selected, children: selected.children || undefined });
    setShowTourModal(true);
  };

  const onAddStorageToItinerary = useCallback(
    (storage: Providers.RadicalStorage.StorageItem) => {
      setStorageModalData({
        storageName: storage.name,
        storageCoordinate: {
          lat: Number(storage.coordinate.lat),
          lng: Number(storage.coordinate.lng),
        },
        storageAddress: tripReference?.city.name || "",
      });
      setShowStoragePoiModal(true);
    },
    [tripReference]
  );

  return (
    <>
      <AppNav
        tripInfoHeader={false}
        tripAppMenu={
          <TripAppMenu
            onClickBookings={() => {
              focusLost();
              if (providers.kiwi) {
                setKiwiFavoritesTab("bookings");
                openContainer(setFavoritesVisible);
              } else {
                openContainer(setYourBookingsVisible);
              }
              setShowTourModal(false);
            }}
            onClickOffers={() => {
              focusLost();
              if (providers.kiwi) {
                setKiwiFavoritesTab("offers");
                openContainer(setFavoritesVisible);
              } else {
                openContainer(setOffersMyVisible);
              }
              setShowTourModal(false);
            }}
            onClickFavorites={() => {
              focusLost();
              setKiwiFavoritesTab("favorites");
              openContainer(setFavoritesVisible);
              setShowTourModal(false);
            }}
            isInTripPlan={tripReference !== undefined}
            sharedTrip={sharedTrip}
            t={t}
          />
        }
        centerContent={
          window.tconfig.GUEST_LOGIN && tripUrl ? (
            <GuestShareHeader
              tripUrl={tripUrl}
              onMountShare={() => {
                if (tripReference && tripReference.shared === false) {
                  switchCheckedOnchange(true);
                }
              }}
              t={t}
            />
          ) : undefined
        }
        sharedTrip={sharedTrip}
      />

      {loadingTripReferences ||
      loadingTripReference ||
      loadingPlans ||
      loadingPlan ||
      loadingPoiCategories ? (
        <PageLoading />
      ) : null}

      {tripReference && (
        <div className={classes.container}>
          <div className={tripListVisible ? "hide-s" : ""}>
            <MapLayout
              tripReference={tripReference}
              planDayIndex={dayIndexNumber}
              fullWidth={!tripListVisible}
              alternativesStepId={alternativesStepId}
              poiCategoryGroups={poiCategoryGroups}
              offersSearchVisible={offersSearchVisible}
              setOffersSearchVisible={setOffersSearchVisible}
              loadingSearchOffers={loadingSearchOffers}
              offersResult={offersResult}
              searchOffer={searchOffer}
              sharedTrip={sharedTrip}
              selectedPoiCategoryIds={selectedPoiCategoryIds}
              setSelectedPoiCategoryIds={setSelectedPoiCategoryIds}
              eventCardClicked={(eventid: number) =>
                handleTourClick(Model.PROVIDER_ID.VICTORY, eventid.toString())
              }
              setShowCloneModal={() => setShowCloneModal(true)}
              onAddStorageToItinerary={onAddStorageToItinerary}
              t={t}
            />
          </div>
          <div className={!tripListVisible ? "hide-s" : ""}>
            <TripLayout
              tripReference={tripReference}
              // showExplorePlaces={() => setExplorePlacesVisible(true)}
              // showLocalExperiences={() => showLocalExperiences()}
              // showPlanList={() => dispatch(changeTripListVisible(true))}
              // selectedPoiCategoryIds={selectedPoiCategoryIds}
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={setSelectedTabIndex}
              setShowCloneModal={() => setShowCloneModal(true)}
              setShowShareTripModal={() => setShowShareTripModal(true)}
              isFocusOpen={isFocusOpen}
              onFocusLost={focusLost}
              t={t}
            >
              {
                <PlanContainer
                  planDayIndex={dayIndexNumber}
                  changePlanDayIndex={(newDayIndexNumber: number) => {
                    navigate(
                      `${TRIP_PLAN.PATH}/${hashParam}/${newDayIndexNumber}`
                    );
                  }}
                  show={selectedTabIndex === 0}
                  alternativesStepId={alternativesStepId}
                  setAlternativesStepId={setAlternativesStepId}
                  // showExplorePlaces={() => {
                  //   setExplorePlacesVisible(true);
                  // }}
                  // showLocalExperiences={() => showLocalExperiences()}
                  // selectedPoiCategoryGroup={(poiCategoryGroup: Model.PoiCategoryGroup) => {
                  //   setSelectedPoiCategoryGroups([poiCategoryGroup]);
                  // }}
                  // selectedPoiCategoryIds={selectedPoiCategoryIds}
                  // poiCategoryGroups={poiCategoryGroups}
                  gygTourIds={gygTourIds}
                  bbTourIds={bbTourIds}
                  viatorTourIds={viatorTourIds}
                  toristyTourIds={toristyTourIds}
                  toursLoading={toursLoading}
                  setShowCloneModal={show => {
                    setShowCloneModal(show);
                  }}
                  onBookingClick={() => setIsBookingClicked(true)}
                />
              }

              {tripReference && plans ? (
                <SearchContainer
                  show={selectedTabIndex === 2}
                  // close={() => setExplorePlacesVisible(false)}
                  dayIndex={dayIndexNumber}
                  tripHash={tripReference.tripHash}
                  tripReadOnly={tripReadOnly}
                  plans={plans}
                  alternatives={alternatives || []}
                  // selectedPoiCategoryGroups={selectedPoiCategoryGroups}
                  poiCategoryGroups={poiCategoryGroups}
                  gygTourIds={gygTourIds}
                  bbTourIds={bbTourIds}
                  viatorTourIds={viatorTourIds}
                  toristyTourIds={toristyTourIds}
                  toursLoading={toursLoading}
                  // sharedTrip={sharedTrip}
                  onBookingClick={() => setIsBookingClicked(true)}
                  t={t}
                />
              ) : null}

              {travelifyUrl ? (
                <TravelifyContainer
                  show={selectedTabIndex === 1}
                  travelifyUrl={travelifyUrl}
                  isLoading={selectedTabIndex === 1 && loadingTravelify}
                  t={t}
                />
              ) : isTourApiEnabled() ? (
                <BookableExperiencesContainer
                  show={selectedTabIndex === 1}
                  tripReference={tripReference}
                  t={t}
                  onTourClick={handleTourClick}
                />
              ) : (
                <BookableExperiencesContainerLegacy
                  show={selectedTabIndex === 1}
                  tripReference={tripReference}
                  t={t}
                  onTourClick={handleTourClick}
                />
              )}

              <KiwiHotelContainer
                show={selectedTabIndex === 3}
                kiwiHotels={kiwiHotels}
                isLoading={selectedTabIndex === 3 && loadingKiwiSearch}
                loadingMore={kiwiLoadingMore}
                hasNextPage={kiwiHasNextPage}
                totalItems={kiwiTotalItems}
                destination={kiwiDestination}
                checkInDate={kiwiCheckIn}
                checkOutDate={kiwiCheckOut}
                adults={kiwiAdults}
                childrenCount={kiwiChildren}
                onDestinationChange={setKiwiDestination}
                onCheckInChange={setKiwiCheckIn}
                onCheckOutChange={setKiwiCheckOut}
                onAdultsChange={setKiwiAdults}
                onChildrenChange={setKiwiChildren}
                onSearch={searchKiwiHotels}
                sortBy={kiwiSortBy}
                onSortChange={setKiwiSortBy}
                filters={kiwiFilters}
                onFiltersChange={setKiwiFilters}
                onLoadMore={kiwiLoadMore}
                cities={cities}
                t={t}
              />
            </TripLayout>
          </div>
        </div>
      )}

      {plans && (
        <FocusContainer
          isBookingClicked={isBookingClicked}
          planId={plans[dayIndexNumber].id}
          isLoadingOffer={isLoadingOffer}
          myAllOffers={myAllOffers}
          offerOptIn={offerOptIn}
          offerOptOut={offerOptOut}
          hideActionButtons={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideFavoriteIcon={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hidePartOfDay={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideFeatures={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideCuisine={sharedTrip && window.tconfig.WIDGET_THEME_1}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          toursLoading={toursLoading}
          timezone={tripReference?.city.timezone}
          onImageModalChange={(isOpen: boolean) => setImageModalOpen(isOpen)}
          onTourClick={handleTourClick}
        />
      )}

      {
        <SearchOffersContainer
          show={offersSearchVisible}
          close={() => {
            setOffersSearchVisible(false);
          }}
          dayIndex={dayIndexNumber}
          plans={plans}
          loadingSearchOffers={loadingSearchOffers}
          offersResult={offersResult}
          isLoadingOffer={isLoadingOffer}
          offerOptIn={offerOptIn}
          offerOptOut={offerOptOut}
          myAllOffers={myAllOffers}
          timezone={tripReference?.city.timezone}
          setShowLoginModal={show => setShowLoginModal(show)}
        />
      }

      {
        <MyOffersContainer
          show={offersMyVisible}
          close={() => {
            setOffersMyVisible(false);
          }}
          dayIndex={dayIndexNumber}
          plans={plans}
          loadingMyAllOffers={loadingMyAllOffers}
          myAllOffers={myAllOffers}
          isLoadingOffer={isLoadingOffer}
          offerOptIn={offerOptIn}
          offerOptOut={offerOptOut}
          timezone={tripReference?.city.timezone}
        />
      }

      {tripReference && plans ? (
        providers.kiwi ? (
          <KiwiFavoritesContainer
            show={favoritesVisible}
            initialTab={kiwiFavoritesTab}
            dayIndex={dayIndexNumber}
            tripHash={tripReference.tripHash}
            tripReadOnly={tripReadOnly}
            plans={plans}
            poiCategoryGroups={poiCategoryGroups}
            gygTourIds={gygTourIds}
            bbTourIds={bbTourIds}
            viatorTourIds={viatorTourIds}
            toristyTourIds={toristyTourIds}
            toursLoading={toursLoading}
            topOffset="90px"
            timezone={tripReference?.city.timezone}
            close={() => setFavoritesVisible(false)}
            onBookingClick={() => setIsBookingClicked(true)}
          />
        ) : (
          <FavoritesContainer
            show={favoritesVisible}
            dayIndex={dayIndexNumber}
            tripHash={tripReference.tripHash}
            tripReadOnly={tripReadOnly}
            plans={plans}
            isFocusOpen={isFocusOpen}
            gygTourIds={gygTourIds}
            bbTourIds={bbTourIds}
            viatorTourIds={viatorTourIds}
            toristyTourIds={toristyTourIds}
            toursLoading={toursLoading}
            close={() => setFavoritesVisible(false)}
            onBookingClick={() => setIsBookingClicked(true)}
          />
        )
      ) : null}

      <MyBookingsContainer
        show={yourBookingsVisible}
        close={() => setYourBookingsVisible(false)}
      />

      {isFocusOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-10 z-50"
          onClick={focusLost}
          aria-label="Close focus panel"
        />
      )}

      <Menu
        listShown={tripListVisible}
        sharedTrip={sharedTrip}
        isInTripPlan={tripReference !== undefined}
        showList={() => {
          dispatch(changeTripListVisible(!tripListVisible));
        }}
        showFavorites={() => {
          focusLost();
          openContainer(setFavoritesVisible);
        }}
        showOffers={() => openContainer(setOffersMyVisible)}
        showBookings={() => openContainer(setYourBookingsVisible)}
        showProfile={() => {
          navigate(USER_PROFILE.PATH);
        }}
        t={t}
      />

      {videreoModal}

      {showLoginModal && (
        <LoginModal
          sharedTripHash={tripReference?.tripHash ?? ""}
          onCancel={() => setShowLoginModal(false)}
        />
      )}
      {showCloneModal && (
        <CloneModal
          sharedTripHash={tripReference?.tripHash ?? ""}
          onCancel={() => setShowCloneModal(false)}
        />
      )}
      {showShareTripModal && tripReference && (
        <>
          <ShareTripModal
            onCancel={() => setShowShareTripModal(false)}
            loading={shareTripModalLoading}
            switchChecked={tripReference.shared}
            onChange={switchCheckedOnchange}
            cityName={tripReference?.city.name}
            arrivalDatetime={tripReference.tripProfile.arrivalDatetime}
            departureDatetime={tripReference.tripProfile.departureDatetime}
            tripHash={tripReference.tripHash}
            dayIndex={dayIndexNumber}
          />
        </>
      )}

      {/* {showTourModal && selectedTour && (
        <Modal
          show={showTourModal}
          className="!w-[90%] !max-w-[100vw] bg-background-color md:!w-[90%] md:!max-w-[90vw] max-h-[90vh] overflow-auto"
          backdropClick={() => setShowTourModal(false)}
          zIndex={499}
        >
          <div className="p-4">
            <div className="flex justify-end mb-4">
              <CloseIconButton2 fill="#2B2B33" clicked={() => setShowTourModal(false)} rounded />
            </div>
            {selectedTour.provider === Model.PROVIDER_ID.VIATOR && (
              <ViatorTourInfo
                tourId={selectedTour.tourId}
                cityId={selectedTour.cityId || 0}
                cityName={selectedTour.cityName}
                startDate={selectedTour.startDate}
                endDate={selectedTour.endDate}
                adults={selectedTour.adults}
                children={selectedTour.children}
              />
            )}
            {selectedTour.provider === Model.PROVIDER_ID.VICTORY && <VictoryProductInfo productId={selectedTour.tourId} />}
            {selectedTour.provider === Model.PROVIDER_ID.TORISTY && <ToristyTourInfo tourId={selectedTour.tourId} t={t} />}
          </div>
        </Modal>
      )} */}

      {selectedTour &&
        selectedTour.provider === Model.PROVIDER_ID.VICTORY &&
        showTourModal && (
          <VictoryProductInfo
            productId={selectedTour.tourId}
            isOpen={
              showTourModal &&
              selectedTour.provider === Model.PROVIDER_ID.VICTORY
            }
            onClose={() => setShowTourModal(false)}
          />
        )}
      {selectedTour &&
        selectedTour.provider === Model.PROVIDER_ID.TORISTY &&
        showTourModal && (
          <ToristyTourInfo
            tourId={selectedTour.tourId}
            isOpen={true}
            onClose={() => {
              setShowTourModal(false);
              setSelectedTour(null);
            }}
          />
        )}
      {selectedTour &&
        selectedTour.provider === Model.PROVIDER_ID.LIVN &&
        showTourModal && (
          <LivnTourInfo
            tourId={Number(selectedTour.tourId)}
            startDate={selectedTour.startDate}
            endDate={selectedTour.endDate}
            isOpen={true}
            onClose={() => {
              setShowTourModal(false);
              setSelectedTour(null);
            }}
          />
        )}
      {selectedTour &&
        selectedTour.provider === Model.PROVIDER_ID.VIATOR &&
        showTourModal && (
          <VistorTourInfo
            tourId={selectedTour.tourId}
            cityId={selectedTour.cityId || 0}
            cityName={selectedTour.cityName}
            startDate={selectedTour.startDate}
            endDate={selectedTour.endDate}
            adults={selectedTour.adults}
            children={selectedTour.children}
            previousRoute={`${TRIP_PLAN.PATH}/${hashParam}`}
            isOpen={
              showTourModal &&
              selectedTour.provider === Model.PROVIDER_ID.VIATOR
            }
            onClose={() => setShowTourModal(false)}
          />
        )}
      {selectedTour &&
        selectedTour.provider === Model.PROVIDER_ID.GYG &&
        showTourModal && (
          <GygTourInfo
            tourId={Number(selectedTour.tourId)}
            cityId={selectedTour.cityId || 0}
            cityName={selectedTour.cityName}
            startDate={selectedTour.startDate}
            endDate={selectedTour.endDate}
            adults={selectedTour.adults}
            children={selectedTour.children}
            isOpen={
              showTourModal && selectedTour.provider === Model.PROVIDER_ID.GYG
            }
            onClose={() => setShowTourModal(false)}
            t={t}
          />
        )}

      {plans && storageModalData && (
        <StoragePoiModal
          dayIndex={dayIndexNumber}
          plansIdDate={plans.map(p => ({ id: p.id, date: p.date }))}
          showModal={showStoragePoiModal}
          setShowModal={() => setShowStoragePoiModal(!showStoragePoiModal)}
          storageName={storageModalData.storageName}
          storageCoordinate={storageModalData.storageCoordinate}
          storageAddress={storageModalData.storageAddress}
        />
      )}
    </>
  );
};

export default TripPlanPage;
